// Central definition of user roles used across the admin UI.
// If backend introduces new roles, add them here once.

export const ROLES = Object.freeze([
  'CUSTOMER',
  'VENDOR',
  'DELIVERER',
  'ADMIN',
  'SUPER_ADMIN',
  'LICENSE_REVIEWER',
  'POS_MANAGER',
  'POS_OPERATOR'
]);

// Certain roles are mutually exclusive (e.g., VENDOR vs DELIVERER) based on current UI logic.
// Added CUSTOMER to exclusivity based on business rule: upgrading to VENDOR/DELIVERER removes CUSTOMER.
const EXCLUSIVE_SETS = [new Set(['VENDOR', 'DELIVERER', 'CUSTOMER'])];

export function toggleRole(currentRoles, role) {
  const has = currentRoles.includes(role);
  let next = has ? currentRoles.filter(r => r !== role) : [...currentRoles, role];
  if (!has) {
    // Enforce exclusivity sets
    for (const set of EXCLUSIVE_SETS) {
      if (set.has(role)) {
        next = next.filter(r => !(set.has(r) && r !== role));
      }
    }
  }
  // Deduplicate defensively
  return Array.from(new Set(next));
}

// Map any backend-provided role variant to our canonical set above.
// Keep this as the single source of truth for role normalization across the app.
export function normalizeRole(rawRole) {
  if (!rawRole) return null;
  const s = String(rawRole).toUpperCase();
  if (s.includes('SUPER_ADMIN')) return 'SUPER_ADMIN';
  if (s.includes('LICENSE_REVIEWER')) return 'LICENSE_REVIEWER';
  if (s.includes('POS_MANAGER')) return 'POS_MANAGER';
  if (s.includes('POS_OPERATOR')) return 'POS_OPERATOR';
  if (s.includes('ADMIN')) return 'ADMIN';
  if (s.includes('DELIVER')) return 'DELIVERER'; // DELIVER, DELIVERER
  if (s.includes('DRIVER') || s.includes('COURIER') || s.includes('RIDER')) return 'DELIVERER';
  if (s.includes('VENDOR') || s.includes('MERCHANT') || s.includes('SELLER')) return 'VENDOR';
  if (s.includes('CUSTOMER') || s.includes('USER')) return 'CUSTOMER';
  return s; // fallback to raw uppercased value
}

export function normalizeRoles(roles) {
  if (!Array.isArray(roles)) return [];
  let out = Array.from(new Set(roles.map(normalizeRole).filter(Boolean)));
  // Enforce mutual exclusivity logic (matching EXCLUSIVE_SETS)
  const hasVendor = out.includes('VENDOR');
  const hasDeliverer = out.includes('DELIVERER');
  
  // If VENDOR, cannot be DELIVERER or CUSTOMER
  if (hasVendor) {
    out = out.filter(x => x !== 'DELIVERER' && x !== 'CUSTOMER');
  } 
  // If DELIVERER (and not VENDOR, handled above), cannot be CUSTOMER
  else if (hasDeliverer) {
    out = out.filter(x => x !== 'CUSTOMER');
  }
  
  if (out.includes('VENDOR')) out = out.filter((x) => x !== 'DELIVERER');
  return out;
}
